




















































import { Component, Vue, Ref } from "vue-property-decorator";
import { Route } from "vue-router";
import LoggingHistoryConfigureWrapper from "@/components/knockoutWrappers/LoggingHistoryConfigureWrapper.vue";
import FeatureNotAvailable from "@/components/FeatureNotAvailable.vue";
import LoggingHistoryGraph, { LoggingHistoryGraphQueryData } from "@/components/LoggingHistoryGraph.vue";
import Store from "@/store";
import { getModule } from "vuex-module-decorators";
import VesselsModule from "@/store/clients/Vessels.module";
import { LoggingHistoryConfig } from "@/types/loggingHistoryConfig";
import { ExtendedVessel } from "@/types/vessel";
import { CdsMinMaxDates } from "@/types/cds/minMaxDates";
import LoggingHistoryClient from "@/Scripts/clients/logging-history-client";
import { Feature } from "@/types/feature";
import { apiGet } from "@/services/api";

const Vessels = getModule(VesselsModule, Store);

@Component({
  components: {
    LoggingHistoryConfigureWrapper,
    FeatureNotAvailable,
    LoggingHistoryGraph,
  },
  beforeRouteEnter(to: Route, from: Route, next): void {
    function showLoggingHistory(vessel: ExtendedVessel | null): boolean {
      return Boolean(vessel?.logDataMaxDate || vessel?.logDataMinDate || vessel?.onboardSystem === "Nautilus");
    }
    //Case: switching to a vessel from sidebar where logging history is not a menu item - redirect to 'Info'
    if (showLoggingHistory(Vessels.currentVessel)) {
      next();
    } else {
      next({ name: "Info", replace: true });
    }
  },
})
export default class LoggingHistory extends Vue {
  vessel = Vessels.currentVessel;
  queryData: LoggingHistoryGraphQueryData[] = [];
  configPanel: any;
  panelsOpen: 0 | null = 0;

  @Ref() readonly wrapper!: LoggingHistoryConfigureWrapper;

  get featureEnabled(): boolean {
    if (!this.vessel) return false;

    return this.vessel.features.some((feature: Feature) => feature.name === "LoggingHistory");
  }

  async onSubmit(configData: LoggingHistoryConfig): Promise<void> {
    if (!this.vessel) return;

    const logVariableDistinctSplit = configData.logVariableIds?.split(/\|/);
    const cdsFeatureDistinctSplit = configData.cdsFeatureIds?.split(/\|/);
    const logVariableIdsDistinct = logVariableDistinctSplit[0].split(/;/) ?? [];
    const cdsFeatureIdsDistinct = cdsFeatureDistinctSplit[0].split(/;/) ?? [];
    const logVariableIdsGroups = logVariableDistinctSplit.length > 1 ? logVariableDistinctSplit[1].split(/;/) ?? [] : [];
    const cdsFeatureIdsGroups = cdsFeatureDistinctSplit.length > 1 ? cdsFeatureDistinctSplit[1].split(/;/) ?? [] : [];

    let fromDate: string;
    let toDate: string;

    if (configData.timeConfigurationType === "AllValues") {
      if (this.vessel.onboardSystem === "Nautilus") {
        const cdsMinMaxDates = await apiGet<CdsMinMaxDates>(`/Cds/Data/MinMax?vesselId=${this.vessel.id}`);
        fromDate = cdsMinMaxDates.minDate;
        toDate = cdsMinMaxDates.maxDate;
      } else {
        fromDate = this.vessel.logDataMinDate;
        toDate = this.vessel.logDataMaxDate;
      }
    } else if (configData.timeConfigurationType === "LastVoyage") {
      fromDate = configData.lastVoyage!.startDate;
      toDate = configData.lastVoyage!.endDate;
    } else if (configData.timeConfigurationType === "LastPeriod") {
      const fromDateDate = new Date();

      fromDateDate.setUTCDate(fromDateDate.getUTCDate() - configData.lastNumberDays);
      fromDateDate.setUTCHours(fromDateDate.getUTCHours() - configData.lastNumberHours);
      fromDateDate.setUTCMinutes(fromDateDate.getUTCMinutes() - configData.lastNumberMinutes);

      fromDate = fromDateDate.toISOString();
      toDate = new Date().toISOString();
    } else {
      fromDate = configData.fromDateTime;
      toDate = configData.toDateTime;
    }

    const selectedTimeInMinutes = this.minutesDiff(new Date(fromDate), new Date(toDate));

    const granularity = {
      Auto: this.autoGranularity(selectedTimeInMinutes),
      Raw: "Raw",
      Minute: "Minute",
      Quarter: "QuarterHour",
      Hour: "Hour",
      Day: "Day",
    }[configData.dataInterval];

    const filterMinMax = configData.filterMinMax;
    const filterOutliers = configData.filterOutliers;
    const cdsGranularity = this.getCdsGranularity(granularity);

    let queryData: LoggingHistoryGraphQueryData[] = logVariableIdsDistinct.map(logVariableIds => ({
      logDataFilter:
        logVariableIds === ""
          ? undefined
          : {
              logVariableIds,
              fromDate,
              toDate,
              granularity,
              filterMinMax,
              filterOutliers,
            },
      cdsDataQuery: undefined,
    }));

    const cdsQueryData: LoggingHistoryGraphQueryData[] = cdsFeatureIdsDistinct.map(cdsFeatureIds => ({
      logDataFilter: undefined,
      cdsDataQuery:
        cdsFeatureIds === ""
          ? undefined
          : {
              vesselId: this.vessel!.id,
              cdsFeatureIds: cdsFeatureIds,
              fromDate: fromDate,
              toDate: toDate,
              granularity: cdsGranularity,
              filterOutliers: filterOutliers,
            },
    }));
    queryData.push(...cdsQueryData);

    const groupsQueryData: LoggingHistoryGraphQueryData[] = logVariableIdsGroups.map((logVariableIds, i) => ({
      logDataFilter:
        logVariableIds === ""
          ? undefined
          : {
              logVariableIds,
              fromDate,
              toDate,
              granularity,
              filterMinMax,
              filterOutliers,
            },
      cdsDataQuery:
        cdsFeatureIdsGroups[i] === ""
          ? undefined
          : {
              vesselId: this.vessel!.id,
              cdsFeatureIds: cdsFeatureIdsGroups[i],
              fromDate: fromDate,
              toDate: toDate,
              granularity: cdsGranularity,
              filterOutliers: filterOutliers,
            },
    }));
    queryData.push(...groupsQueryData);
    queryData = queryData.filter(q => q.logDataFilter != undefined || q.cdsDataQuery != undefined);

    this.queryData = queryData;
    this.panelsOpen = null;
  }

  getCdsGranularity(granularity: string): string {
    // CDS only has hour and 15s data, but we also add day here, where we aggregate hour data before display.
    switch (granularity) {
      case "Raw":
        return "15s";
      case "Day":
        return "day";
      default:
        return "hour";
    }
  }

  onUpdateTimePeriod({ fromDateTime, toDateTime }: { fromDateTime: number; toDateTime: number }): void {
    this.wrapper.updateTimePeriod(fromDateTime, toDateTime);
  }

  //Auto will set granularity to the most detailed option available.
  //Using same limits that are already defined in graph-config-data-interval.
  autoGranularity(selectedTimeInMinutes: number): string {
    if (selectedTimeInMinutes <= 1440) {
      return "Raw";
    } else if (selectedTimeInMinutes <= 2880) {
      return "Minute";
    } else if (selectedTimeInMinutes <= 20160) {
      return "QuarterHour";
    } else if (selectedTimeInMinutes <= 129600) {
      return "Hour";
    }

    return "Day";
  }

  minutesDiff(fromDate: Date, toDate: Date): number {
    const diff = (fromDate.getTime() - toDate.getTime()) / 1000 / 60;

    return Math.abs(Math.round(diff));
  }

  downloadExcel(): void {
    LoggingHistoryClient.DownloadExcel(Vessels.currentVessel?.id);
  }

  downloadCsv(): void {
    LoggingHistoryClient.DownloadCsv(Vessels.currentVessel?.id);
  }
}
